/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  TextField,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Checkbox,
  Alert,
  Tooltip,
  TablePagination,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  ArrowCircleLeft as ArrowCircleLeftIcon,
  ArrowCircleRight as ArrowCircleRightIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as actions from '../../redux/actions';
import Loading from '../../components/Loading';
import { Error } from '../../components/Error';
import { ViewTitle } from '../../components/ViewTitle';

const HistoryBuy = ({ title, subtitle }) => {
  const dispatch = useDispatch();
  const goldTransaction = useSelector((state) => state.gold);
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(100);
  const [page, setPage] = useState(0);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [materialFilter, setMaterialFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('day');
  const [selectedItems, setSelectedItems] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const getApiParams = (currentPage, currentSize) => {
    const baseParams = {
      name: '',
      page: currentPage,
      size: currentSize,
      cash: true,
    };

    if (periodFilter === 'all') {
      return { ...baseParams, date: '', month: '', year: '' };
    }

    const selectedDate = dayjs(date);
    switch (periodFilter) {
      case 'day':
        return {
          ...baseParams,
          date: selectedDate.format('YYYY-MM-DD'),
          month: '',
          year: '',
        };
      case 'month':
        return {
          ...baseParams,
          date: '',
          month: selectedDate.format('YYYY-MM'),
          year: '',
        };
      case 'year':
        return {
          ...baseParams,
          date: '',
          month: '',
          year: selectedDate.format('YYYY'),
        };
      default:
        return {
          ...baseParams,
          date: selectedDate.format('YYYY-MM-DD'),
          month: '',
          year: '',
        };
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const apiParams = getApiParams(page + 1, size);
        await dispatch(actions.goldTransactionAll(apiParams));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [dispatch, date, page, size, periodFilter]);

  // ----------------------------------------------------------
  //  ใช้ goldTransaction.rows เพียงอย่างเดียว
  // ----------------------------------------------------------
  const combinedData = useMemo(
    () =>
      (goldTransaction?.rows || []).map((row) => ({
        ...row,
        isReport: row?.gold_report?.status === true,
        canSelect: row?.gold_report?.status !== true,
      })),
    [goldTransaction],
  );

  const materialTypes = useMemo(
    () =>
      _.uniq(
        combinedData.flatMap((item) =>
          item?.order
            ?.map((orderItem) => orderItem?.material?.material_type?.name)
            .filter(Boolean),
        ),
      ),
    [combinedData],
  );

  const filteredByMaterial = useMemo(() => {
    if (materialFilter === 'all') return combinedData;
    return combinedData.filter((item) =>
      item?.order?.some(
        (orderItem) =>
          orderItem?.material?.material_type?.name === materialFilter,
      ),
    );
  }, [combinedData, materialFilter]);

  const buyData = useMemo(
    () =>
      filteredByMaterial.filter(
        (item) => item?.gold_transaction_type === 'BUY',
      ),
    [filteredByMaterial],
  );
  const sellData = useMemo(
    () =>
      filteredByMaterial.filter(
        (item) => item?.gold_transaction_type !== 'BUY',
      ),
    [filteredByMaterial],
  );

  const currentTableData = tabValue === 0 ? buyData : sellData;

  const totals = useMemo(() => {
    return filteredByMaterial.reduce(
      (acc, item) => {
        const itemCost = _.sumBy(item?.order || [], 'cost');
        const itemWeight = _.sumBy(item?.order || [], 'weight');
        const itemVat = _.sumBy(item?.order || [], 'vat');

        if (item?.gold_transaction_type === 'BUY') {
          acc.buyCount += 1;
          acc.buyCost += itemCost;
          acc.buyWeight += itemWeight;
          acc.buyVat += itemVat;
        } else {
          acc.saleCount += 1;
          acc.saleCost += itemCost;
          acc.saleWeight += itemWeight;
          acc.saleVat += itemVat;
        }
        return acc;
      },
      {
        buyCount: 0,
        saleCount: 0,
        buyCost: 0,
        saleCost: 0,
        buyWeight: 0,
        saleWeight: 0,
        buyVat: 0,
        saleVat: 0,
      },
    );
  }, [filteredByMaterial]);

  const selectableItems = currentTableData.filter((item) => item.canSelect);

  const handleDetailTransaction = (id) =>
    history.push(`/accounting/history/detail/cash/${id}`);

  const handleDateChange = (days) => {
    if (periodFilter !== 'all') {
      setDate(dayjs(date).add(days, periodFilter).format('YYYY-MM-DD'));
    }
    setPage(0);
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriodFilter(newPeriod);
    if (newPeriod !== 'all') {
      setDate(dayjs().format('YYYY-MM-DD'));
    }
    setPage(0);
  };

  const handleSelectItem = (id) => {
    const item = currentTableData.find((item) => item._id === id);
    if (!item?.canSelect) return;

    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const allIds = selectableItems.map((item) => item._id);
    const allSelected = allIds.every((id) => selectedItems.includes(id));
    setSelectedItems(allSelected ? [] : allIds);
  };

  // ----------------------------------------------------------
  //  สร้างรายงาน โดยใช้เฉพาะ goldTransaction.rows
  // ----------------------------------------------------------
  const onSubmitReport = async () => {
    try {
      if (selectedItems.length === 0) {
        alert('กรุณาเลือกรายการที่ต้องการสร้างรายงาน');
        return;
      }

      const selectedTransactions = goldTransaction.rows.filter((row) =>
        selectedItems.includes(row._id),
      );

      await dispatch(actions.goldReportCreate(selectedTransactions));

      alert('สร้างรายงานสำเร็จ');

      const apiParams = getApiParams(page + 1, size);
      await dispatch(actions.goldTransactionAll(apiParams));

      setSelectedItems([]);
    } catch (err) {
      console.error(err);
    }
  };
  const getNavigationText = () => {
    const navTexts = {
      day: { prev: 'วันก่อนหน้า', next: 'วันถัดไป' },
      month: { prev: 'เดือนก่อนหน้า', next: 'เดือนถัดไป' },
      year: { prev: 'ปีก่อนหน้า', next: 'ปีถัดไป' },
    };
    return navTexts[periodFilter] || { prev: 'ก่อนหน้า', next: 'ถัดไป' };
  };

  const getPeriodDisplayText = () => {
    if (periodFilter === 'all') return 'ข้อมูลทั้งหมด';
    const selectedDate = dayjs(date);
    const formats = {
      day: 'DD MMMM YYYY',
      month: 'MMMM YYYY',
      year: 'YYYY',
    };
    return selectedDate.format(formats[periodFilter]);
  };

  const formatNumber = (value) =>
    Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  if (goldTransaction.isLoading || loading) return <Loading />;

  if (goldTransaction.isCompleted) {
    const navText = getNavigationText();
    return (
      <Box sx={{ p: 2 }}>
        <ViewTitle title={title} subtitle={subtitle} />

        {/* FILTER AREA */}
        <Card sx={{ p: 3, mb: 3, mt: 3 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            เลือกช่วงเวลาและกรองข้อมูล
          </Typography>
          {periodFilter !== 'all' && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowCircleLeftIcon />}
                onClick={() => handleDateChange(-1)}
                sx={{ fontSize: '1rem' }}
              >
                {navText.prev}
              </Button>
              <Typography
                variant="h5"
                sx={{ fontWeight: 'bold', color: '#1976d2' }}
              >
                {getPeriodDisplayText()}
              </Typography>
              <Button
                variant="outlined"
                endIcon={<ArrowCircleRightIcon />}
                onClick={() => handleDateChange(1)}
                sx={{ fontSize: '1rem' }}
              >
                {navText.next}
              </Button>
            </Box>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>ช่วงเวลา</InputLabel>
                <Select
                  value={periodFilter}
                  label="ช่วงเวลา"
                  onChange={(e) => handlePeriodChange(e.target.value)}
                >
                  <MenuItem value="day">รายวัน</MenuItem>
                  <MenuItem value="month">รายเดือน</MenuItem>
                  <MenuItem value="year">รายปี</MenuItem>
                  <MenuItem value="all">ทั้งหมด</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {periodFilter !== 'all' && (
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="เลือกวันที่"
                    inputFormat="DD/MM/YYYY"
                    value={dayjs(date)}
                    onChange={(newValue) => {
                      setDate(newValue.format('YYYY-MM-DD'));
                      setPage(0);
                    }}
                    renderInput={(props) => <TextField {...props} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>ประเภทสินค้า</InputLabel>
                <Select
                  value={materialFilter}
                  label="ประเภทสินค้า"
                  onChange={(e) => setMaterialFilter(e.target.value)}
                >
                  <MenuItem value="all">ทั้งหมด</MenuItem>
                  {materialTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {/* SUMMARY */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, bgcolor: '#ffebee', height: '100%' }}>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: 'bold', color: '#d32f2f' }}
              >
                สรุปรายการซื้อ (รับเข้า)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  จำนวนรายการ: {totals.buyCount}
                </Grid>
                <Grid item xs={6}>
                  น้ำหนักรวม: {totals.buyWeight.toFixed(3)} กรัม
                </Grid>
                <Grid item xs={6}>
                  มูลค่ารวม: {formatNumber(totals.buyCost)} บาท
                </Grid>
                <Grid item xs={6}>
                  ภาษีรวม: {formatNumber(totals.buyVat)} บาท
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, bgcolor: '#e8f5e9', height: '100%' }}>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}
              >
                สรุปรายการขาย (ออก)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  จำนวนรายการ: {totals.saleCount}
                </Grid>
                <Grid item xs={6}>
                  น้ำหนักรวม: {totals.saleWeight.toFixed(3)} กรัม
                </Grid>
                <Grid item xs={6}>
                  มูลค่ารวม: {formatNumber(totals.saleCost)} บาท
                </Grid>
                <Grid item xs={6}>
                  ภาษีรวม: {formatNumber(totals.saleVat)} บาท
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* TABLE */}
        <Card sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={(e, v) => {
                setTabValue(v);
                setSelectedItems([]);
                setPage(0);
              }}
              variant="fullWidth"
            >
              <Tab label={`รายการซื้อ (${totals.buyCount})`} />
              <Tab label={`รายการขาย (${totals.saleCount})`} />
            </Tabs>
          </Box>

          <Box sx={{ p: 2, bgcolor: '#f8f9fa' }}>
            <Button
              variant="outlined"
              onClick={handleSelectAll}
              size="small"
              disabled={selectableItems.length === 0}
            >
              {selectableItems.every((i) => selectedItems.includes(i._id))
                ? 'ยกเลิกเลือกทั้งหมด'
                : 'เลือกทั้งหมด'}
            </Button>

            {selectedItems.length > 0 && (
              <Button
                variant="contained"
                color="success"
                sx={{ ml: 2 }}
                onClick={onSubmitReport}
              >
                สร้างรายงาน ({selectedItems.length})
              </Button>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#eee' }}>
                  <TableCell>
                    <Checkbox
                      checked={
                        selectableItems.length > 0 &&
                        selectableItems.every((item) =>
                          selectedItems.includes(item._id),
                        )
                      }
                      indeterminate={
                        selectedItems.length > 0 &&
                        selectedItems.length < selectableItems.length
                      }
                      onChange={handleSelectAll}
                      disabled={selectableItems.length === 0}
                    />
                  </TableCell>
                  <TableCell>ลำดับ</TableCell>
                  <TableCell>ประเภท</TableCell>
                  <TableCell>รายการ</TableCell>
                  <TableCell>มูลค่า</TableCell>
                  <TableCell>ภาษี</TableCell>
                  <TableCell>น้ำหนัก</TableCell>
                  <TableCell>วันที่</TableCell>
                  <TableCell>การดำเนินการ</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {currentTableData.map((row, index) => {
                  const isSelected = selectedItems.includes(row._id);
                  const isBuy = row?.gold_transaction_type === 'BUY';

                  return (
                    <TableRow
                      key={row._id}
                      sx={{
                        bgcolor: row.gold_report?.status
                          ? '#f5f5f5'
                          : isSelected
                          ? isBuy
                            ? '#ffebee'
                            : '#e8f5e9'
                          : 'inherit',
                      }}
                    >
                      <TableCell>
                        {!row.canSelect ? (
                          <Tooltip title="รายการนี้ถูกสร้างรายงานแล้ว">
                            <BlockIcon color="warning" />
                          </Tooltip>
                        ) : (
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectItem(row._id)}
                          />
                        )}
                      </TableCell>

                      <TableCell>{index + 1 + page * size}</TableCell>

                      <TableCell>
                        <Chip
                          label={isBuy ? 'ซื้อ' : 'ขาย'}
                          color={isBuy ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        {row?.order?.length > 0 ? (
                          <Box>
                            {row.order.map((item, idx) => (
                              <Box key={idx}>
                                <Chip
                                  label={`${item?.material?.name || '-'}${
                                    item?.place === 'คลังรูปพรรณใหม่'
                                      ? 'ใหม่'
                                      : item?.place
                                      ? ` ${item.place}`
                                      : ''
                                  }(${
                                    item?.material?.material_type?.name || '-'
                                  })`}
                                  variant="outlined"
                                  size="small"
                                  sx={{ display: 'inline-flex', mb: 0.5 }}
                                />
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Chip
                            label="ไม่มีสินค้า"
                            color="default"
                            size="small"
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        {row?.order?.map((item, i) => (
                          <div key={i}>{formatNumber(item.cost)}</div>
                        ))}
                      </TableCell>

                      <TableCell>
                        {row?.order?.map((item, i) => (
                          <div key={i}>{formatNumber(item.vat)}</div>
                        ))}
                      </TableCell>

                      <TableCell>
                        {row?.order?.map((item, i) => (
                          <div key={i}>{item.weight.toFixed(3)}</div>
                        ))}
                      </TableCell>

                      <TableCell>
                        {dayjs(row?.date).format('DD/MM/YYYY')}
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="outlined"
                          startIcon={<DescriptionIcon />}
                          size="small"
                          onClick={() => handleDetailTransaction(row._id)}
                        >
                          รายละเอียด
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <TablePagination
              component="div"
              count={currentTableData.length}
              page={page}
              rowsPerPage={size}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="แสดงต่อหน้า:"
              sx={{
                '& .MuiTablePagination-displayedRows': {
                  display: 'none',
                },
                '& .MuiTablePagination-actions': {
                  display: 'none',
                },
              }}
            />
          </Box>
        </Card>
      </Box>
    );
  }

  return <Error message={goldTransaction?.message} />;
};

HistoryBuy.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

HistoryBuy.defaultProps = {
  title: '',
  subtitle: '',
};

export default HistoryBuy;
